import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { FaCheck, FaExchangeAlt, FaEye, FaMoneyBillWave, FaPrint } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PageHeading from "../PageHeading";
import AddBill from "./AddBill";
import axios from "axios";
import { saveToCache, readFromCache } from "../../services/pathologyCache";
import { getLocalBills } from "../../services/pathologyOutbox";
import { onSyncStatusChange } from "../../services/pathologySyncEngine";
import BillDetailsModal from "./BillDetailsModal";
import TableSkeleton from "../TableSkeleton";
import ConfirmActionModal from "../ConfirmDeleteModal";
import { FiEdit } from "react-icons/fi";
import { useAuth } from "../../utils/AuthContext";
import SmartDropdown from "../Shared/SmartDropdown";
import { BsThreeDotsVertical } from "react-icons/bs";
import PathologyAddPaymentModal from "./PathologyAddPaymentModal";
import { PathologyPrintSlip } from "./PathologyPrintSlip";
import DynamicSelect from "../DynamicSelect";
import AccountExportOptions from "../AccountExportOptions";
import Pagination from "../Shared/Pagination";

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const API_URL = "https://api.muktihospital.com/api/pathology-bill";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const PathologyBill = () => {
  const { user } = useAuth();
  const isReadOnly = user?.role === "hospital_manager";
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [printSlipData, setPrintSlipData] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dropdownTriggerRefs = useRef({});
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentBillData, setPaymentBillData] = useState(null);
  const [generateBillModalOpen, setGenerateBillModalOpen] = useState(false);
  const [bills, setBills] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [billDetails, setBillDetails] = useState(null);
  const [isBillDetailsModalOpen, setIsBillDetailsModalOpen] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [billToEdit, setBillToEdit] = useState(null); 
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [billToCancel, setBillToCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [syncFilter, setSyncFilter] = useState("all");
  const [dateFilterType, setDateFilterType] = useState("daily");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [allBillsForExport, setAllBillsForExport] = useState([]);
  const itemsPerPage = 10;
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const isAdmin = user?.role === "admin";
  const isSuperAdmin = user?.role === "superadmin" || user?.role === "owner";

  const canDeleteBill = (bill) => {
    if (isAdmin || isSuperAdmin) return true;
    if (!user?.name || !bill?.userName) return false;

    const myName = user.name.toLowerCase().trim();
    const billOwner = bill.userName.toLowerCase().trim();

    return billOwner === myName || billOwner.includes(myName.split(" ")[0]);
  };

  const handleAddMoney = (bill) => {
    setPaymentBillData(bill);
    setPaymentModalOpen(true);
  };

  const filterOptions = [
    { value: "Active", label: "Active (All)" },
    { value: "Pending", label: "Pending (Due)" },
    { value: "Complete", label: "Complete (Paid)" },
    { value: "Cancelled", label: "Cancelled" },
    { value: "All", label: "All History" },
  ];

  const filteredBills = useMemo(() => {
    if (syncFilter === "pending") {
      return bills.filter(
        (b) =>
          b.__offline ||
          (typeof b.billNumber === "string" &&
            b.billNumber.startsWith("MH_PB_OFF"))
      );
    }
    return bills;
  }, [bills, syncFilter]);

  const fetchBills = useCallback(async () => {
    const mapLocalBillToRow = (lb) => {
      const p = lb.payload || {};
      return {
        id: `offline-${lb.clientId}`,
        billNumber: lb.provisionalBillNumber || "OFFLINE",
        createdAt: lb.createdAt,
        patientName: p.patientName,
        doctorName: p.doctorName,
        netAmount: p.netAmount,
        dueAmount: p.dueAmount,
        paymentAmount: p.paymentAmount,
        status: lb.syncedBillNumber ? "Complete" : "Pending Sync",
        userName: p.userName,
        authorizedBy: p.authorizedBy,
        __offline: true, 
      };
    };

    setIsLoading(true);
    // Local (offline-created) bills that are still unsynced
    let localRows = [];
    try {
      const locals = await getLocalBills();
      localRows = locals
        .filter((lb) => !lb.syncedBillNumber)
        .map(mapLocalBillToRow);
    } catch (e) {
      localRows = [];
    }

    try {
      const dateParams = new URLSearchParams();
      if (dateFilterType === "daily") {
        dateParams.set("startDate", startDate);
        dateParams.set("endDate", endDate);
      } else if (dateFilterType === "monthly") {
        const [y, m] = selectedMonth.split("-");
        dateParams.set("year", y);
        dateParams.set("month", m);
      } else if (dateFilterType === "yearly") {
        dateParams.set("year", selectedYear);
      }
      const dateQuery = dateParams.toString() ? `&${dateParams.toString()}` : "";

      const url = `${API_URL}?page=${currentPage}&limit=${itemsPerPage}&search=${debouncedSearchQuery}&status=${statusFilter}${dateQuery}`;

      const response = await axios.get(url, {
        headers: { "x-api-key": API_KEY },
      });
      const serverBills = response.data.bills || [];
      // Cache only the first page of the default view for offline viewing
      if (currentPage === 1 && !debouncedSearchQuery && dateFilterType === "all") {
        saveToCache(`bills_${statusFilter}`, serverBills);
      }
      // Show offline pending bills on top, then the server bills
      setBills([...localRows, ...serverBills]);
      setTotalPages(response.data.pagination.totalPages || 1);
      setTotalRecords(response.data.pagination.totalRecords || 0);
    } catch (err) {
      console.error("Error fetching bills:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      // Offline fallback: cached server bills + local pending bills
      const cached = (await readFromCache(`bills_${statusFilter}`)) || [];
      if (localRows.length > 0 || cached.length > 0) {
        setBills([...localRows, ...cached]);
        setTotalPages(1);
        setTotalRecords(localRows.length + cached.length);
        toast.info("Offline: showing saved & pending bills.");
      } else if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        // Offline with nothing cached yet: show empty list quietly,
        // do NOT show a scary error toast (bill was still saved to outbox).
        setBills([...localRows]);
        setTotalPages(1);
        setTotalRecords(localRows.length);
      } else {
        toast.error(err.response?.data?.error || "Failed to load bills.");
      }
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, debouncedSearchQuery, statusFilter, dateFilterType, startDate, endDate, selectedMonth, selectedYear]);

  // Re-fetch when page / search / filter changes
  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  // Auto-refresh the list when the sync engine finishes pushing offline bills
  useEffect(() => {
    const unsub = onSyncStatusChange((status) => {
      if (status && status.justSynced && status.justSynced > 0) {
        // Small delay so the server commit + local outbox update settle
        // before we re-fetch, so the freshly-synced bills show up.
        setTimeout(() => {
          fetchBills();
        }, 1200);
      }
    });
    return () => unsub();
  }, [fetchBills]);

  useEffect(() => {
    if (debouncedSearchQuery && filteredBills.length === 0 && !isLoading) {
      toast.dismiss("no-bills-toast");
      toast.info("No bills found matching the search query.", {
        toastId: "no-bills-toast",
      });
    }
  }, [debouncedSearchQuery, filteredBills, isLoading]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSelectAll = (e) => {
    if (isReadOnly) return;
    if (e.target.checked) {
      const allowedBills = filteredBills.filter((bill) => !bill.__offline && canDeleteBill(bill));
      setSelectedRows(allowedBills.map((bill) => bill.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (billId) => {
    if (isReadOnly) return;
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(billId)
        ? prevSelectedRows.filter((id) => id !== billId)
        : [...prevSelectedRows, billId]
    );
  };

 const handleSingleDelete = (billId) => {
    setSelectedRows([billId]);
    setDeleteModalOpen(true);
  };

  const handleCancelBill = async () => {
  if (!cancelReason || cancelReason.length < 5) {
    toast.error("Please provide a valid reason for cancellation.");
    return;
  }

  setIsLoading(true);
  try {
    await axios.post(`${API_URL}/cancel/${billToCancel.id}`, {
      reason: cancelReason,
      userName: user?.name || "Unknown User"
    }, {
      headers: { "x-api-key": API_KEY }
    });

    toast.success("Bill cancelled and refund logged successfully.");
    setCancelModalOpen(false);
    setCancelReason("");
    handleRefreshBills();
  } catch (err) {
    toast.error(err.response?.data?.error || "Failed to cancel bill.");
  } finally {
    setIsLoading(false);
  }
};

  const handleEdit = async (billId) => {
    try {
      const response = await axios.get(`${API_URL}/${billId}`, {
        headers: { "x-api-key": API_KEY },
      });
      setBillToEdit(response.data.bill);
      setIsEditModalOpen(true);
    } catch (err) {
      console.error("Error fetching bill for edit:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      toast.error(err.response?.data?.error || "Failed to load bill for editing.");
    }
  };

 const handleDelete = async (billIds) => {
    try {
      const deleteConfig = {
        headers: { "x-api-key": API_KEY },
        data: {
          userName: user?.name || user?.username || "",
          role: user?.role || "",
        },
      };

      if (Array.isArray(billIds)) {
        await Promise.all(
          billIds.map((id) =>
            axios.delete(`${API_URL}/delete/${id}`, deleteConfig)
          )
        );
      } else {
        await axios.delete(`${API_URL}/delete/${billIds}`, deleteConfig);
      }

      const url = `${API_URL}?page=${currentPage}&limit=${itemsPerPage}`;
      const response = await axios.get(url, {
        headers: { "x-api-key": API_KEY },
      });
      setBills(response.data.bills || []);

      setTotalPages(response.data.pagination.totalPages || 1);
      setTotalRecords(response.data.pagination.totalRecords || 0);
      setSelectedRows([]);
      toast.success(
        `${Array.isArray(billIds) ? billIds.length : 1} bill(s) deleted successfully!`
      );
    } catch (err) {
      console.error("Error deleting bills:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      toast.error(err.response?.data?.error || "Failed to delete bill(s).");
    }
  };

  const numberToWordsBD = (value) => {
    let num = Math.floor(Number(value) || 0);
    if (num === 0) return "Zero";
    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const two = (n) => (n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : ""));
    const three = (n) => {
      const h = Math.floor(n / 100), r = n % 100;
      return (h ? ones[h] + " Hundred" + (r ? " " : "") : "") + (r ? two(r) : "");
    };
    let words = "";
    const crore = Math.floor(num / 10000000); num %= 10000000;
    const lakh = Math.floor(num / 100000); num %= 100000;
    const thousand = Math.floor(num / 1000); num %= 1000;
    if (crore) words += three(crore) + " Crore ";
    if (lakh) words += two(lakh) + " Lakh ";
    if (thousand) words += two(thousand) + " Thousand ";
    if (num) words += three(num);
    return words.trim();
  };

  const parseTestsData = (t) => {
    if (Array.isArray(t)) return t;
    if (typeof t === "string") { try { const p = JSON.parse(t); return Array.isArray(p) ? p : []; } catch { return []; } }
    return [];
  };

  const handlePrintBill = async (bill) => {
    try {
      const res = await axios.get(`${API_URL}/${bill.id}`, { headers: { "x-api-key": API_KEY } });
      const bd = res.data.bill || bill;

      let phone = bd.patient?.phoneNumber || "";
      const pid = bill.patientId || bd.patientId;
      if (pid) {
        try {
          const pRes = await axios.get(API_URL.replace("pathology-bill", "patient"), { headers: { "x-api-key": API_KEY } });
          const list = Array.isArray(pRes.data) ? pRes.data : Array.isArray(pRes.data.data) ? pRes.data.data : [];
          const found = list.find((p) => String(p.id) === String(pid));
          if (found?.phoneNumber) phone = found.phoneNumber;
        } catch (e) { /* ignore */ }
      }

      const tests = parseTestsData(bd.tests);
      const total = Number(bd.total) || 0;
      const discount = Number(bd.discount) || 0;
      const netAmount = Number(bd.netAmount) || 0;
      const paidAmount = Number(bd.paymentAmount) || 0;
      const dueAmount = bd.dueAmount != null ? Number(bd.dueAmount) : Math.max(0, netAmount - paidAmount);
      const isPaid = dueAmount <= 0;
      const isFree = netAmount === 0 && total > 0;

      setPrintSlipData({
        title: "Pathology Invoice",
        isPaid,
        isFree,
        amountInWords: isFree
          ? "Zero Taka Only (100% Hospital Free)"
          : numberToWordsBD(netAmount) + " Taka Only",
        note: (bd.freeBillReason || bd.note) ? (
          <div className="note-box">
            <strong>Note:</strong> {bd.freeBillReason || bd.note}
          </div>
        ) : null,
        billInfo: (
          <>
            <div>
              <p><strong>Bill No:</strong> {bd.billNumber || "N/A"}</p>
              <p><strong>Patient Name:</strong> {bd.patientName || "N/A"}</p>
              <p><strong>Mobile:</strong> {phone || "N/A"}</p>
            </div>
            <div>
              <p>
                <strong>Date:</strong>{" "}
                {bd.createdAt
                  ? `${new Date(bd.createdAt).toLocaleDateString("en-GB")} ${new Date(bd.createdAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "numeric", hour12: true })}`
                  : "N/A"}
              </p>
              <p><strong>Age:</strong> {bd.age || "N/A"}</p>
              <p><strong>Donor:</strong> {bd.donorName || "N/A"}</p>
            </div>
            <p className="full"><strong>Doctor Name:</strong> {bd.doctorName || "N/A"}</p>
          </>
        ),
        table: (
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 sm:p-3 border font-semibold">Test Name</th>
                <th className="p-2 sm:p-3 border font-semibold text-right">Amount (৳)</th>
              </tr>
            </thead>
            <tbody>
              {tests.map((t, i) => (
                <tr key={i} className="border-t">
                  <td className="p-2 sm:p-3 border">{t.testName || "N/A"}</td>
                  <td className="p-2 sm:p-3 border text-right">{(Number(t.amount) || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ),
        summary: (
          <div className="flex flex-col">
            <div className="flex justify-between"><span>Total:</span><span>৳{total.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Discount ({bd.discountType === "percentage" ? "%" : "৳"}):</span><span>{discount.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Net Amount:</span><span>৳{netAmount.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Net Payable:</span><span>৳{paidAmount.toFixed(2)}</span></div>
            {dueAmount > 0 && (
              <div className="flex justify-between"><span>Due:</span><span>৳{dueAmount.toFixed(2)}</span></div>
            )}
          </div>
        ),
        footer: (
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: "30px", paddingLeft: "10px", paddingRight: "10px" }}>
            <div style={{ textAlign: "center", width: "220px" }}>
              <div style={{ borderTop: "1px solid #000", marginBottom: "8px" }}></div>
              <p style={{ margin: 0, fontWeight: "bold", fontSize: "11pt", textTransform: "capitalize" }}>{bd.userName || "N/A"}</p>
              <p style={{ margin: "5px 0 0", fontSize: "9pt" }}>Prepared By</p>
            </div>
            <div style={{ textAlign: "center", width: "220px" }}>
              <div style={{ borderTop: "1px solid #000", marginBottom: "8px" }}></div>
              <p style={{ margin: 0, fontWeight: "bold", fontSize: "11pt" }}>{bd.authorizedBy || "N/A"}</p>
              <p style={{ margin: "5px 0 0", fontSize: "9pt" }}>Authorized By</p>
            </div>
          </div>
        ),
      });
    } catch (err) {
      console.error("Print bill error:", err);
      toast.error("Failed to load bill for printing.");
    }
  };

  // When slip data is ready, render then print.
  useEffect(() => {
    if (!printSlipData) return;
    const t = setTimeout(() => {
      window.print();
      setTimeout(() => setPrintSlipData(null), 400);
    }, 120);
    return () => clearTimeout(t);
  }, [printSlipData]);

  const handleView = async (bill) => {
    try {
      const response = await axios.get(`${API_URL}/${bill.id}`, {
        headers: { "x-api-key": API_KEY },
      });
      const billData = response.data.bill;
      const normalizedBill = {
        ...billData,
        createdAt: billData.createdAt || bill.createdAt,
      };
      setBillDetails(normalizedBill);
      setIsBillDetailsModalOpen(true);
    } catch (err) {
      console.error("Error fetching bill details:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      toast.error(err.response?.data?.error || "Failed to load bill details.");
    }
  };

  const handleDeleteFromModal = (billId) => {
    setBills((prevBills) => prevBills.filter((bill) => bill.id !== billId));
    setTotalRecords((prev) => prev - 1);
    setTotalPages(Math.ceil((totalRecords - 1) / itemsPerPage));
    setIsBillDetailsModalOpen(false);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleRefreshBills = async () => {
    try {
      await fetchBills();
      if (fetchAllForExport) fetchAllForExport();
    } catch (err) {
      console.error("Error refreshing bills:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
      if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        return;
      }
      toast.error(err.response?.data?.error || "Failed to refresh bills.");
    }
  };

const fetchAllForExport = useCallback(async () => {
    try {
      const dateParams = new URLSearchParams();
      if (dateFilterType === "daily") {
        dateParams.set("startDate", startDate);
        dateParams.set("endDate", endDate);
      } else if (dateFilterType === "monthly") {
        const [y, m] = selectedMonth.split("-");
        dateParams.set("year", y);
        dateParams.set("month", m);
      } else if (dateFilterType === "yearly") {
        dateParams.set("year", selectedYear);
      }
      const dateQuery = dateParams.toString() ? `&${dateParams.toString()}` : "";

      const url = `${API_URL}?limit=0&search=${debouncedSearchQuery}&status=${statusFilter}${dateQuery}`;
      const response = await axios.get(url, {
        headers: { "x-api-key": API_KEY },
      });
      setAllBillsForExport(response.data.bills || []);
    } catch (err) {
      console.error("Export fetch error:", err);
    }
  }, [debouncedSearchQuery, statusFilter, dateFilterType, startDate, endDate, selectedMonth, selectedYear]);

useEffect(() => {
    fetchAllForExport();
}, [fetchAllForExport]);

const getFilterSubtitle = () => {
    let dateText = "All Time";
    if (dateFilterType === "daily") {
      const fStart = new Date(startDate).toLocaleDateString("en-GB");
      const fEnd = new Date(endDate).toLocaleDateString("en-GB");
      dateText = fStart === fEnd ? fStart : `${fStart} - ${fEnd}`;
    } else if (dateFilterType === "monthly") {
      const [year, month] = selectedMonth.split("-");
      const dateObj = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
      dateText = dateObj.toLocaleString("en-US", { month: "long", year: "numeric" });
    } else if (dateFilterType === "yearly") {
      dateText = `Year ${selectedYear}`;
    }
    return `Period: ${dateText}  |  Status: ${statusFilter}`;
  };

  const totalNetAmount = allBillsForExport.reduce((sum, b) => sum + (Number(b.netAmount) || 0), 0);
  const totalDueAmount = allBillsForExport.reduce((sum, b) => sum + (Number(b.dueAmount) || 0), 0);

  const billSummaryFooter = [
    [
      { content: "TOTAL", colSpan: 5, styles: { fontStyle: "bold", halign: "left" } },
      { content: totalNetAmount.toFixed(2), styles: { fontStyle: "bold", halign: "right" } },
      { content: totalDueAmount.toFixed(2), styles: { fontStyle: "bold", halign: "right" } },
      { content: "", styles: { halign: "center" } },
    ],
  ];

const headers = [
    'SL No',
    "BILL NO",
    "DATE",
    "PATIENT NAME",
    "DOCTOR NAME",
    "NET AMOUNT",
    "DUE AMOUNT",
    "STATUS"
];

const exportDataMapper = (item) => {
    const index = allBillsForExport.findIndex(b => b.id === item.id);
    return [
        index + 1,
        item.billNumber,
        new Date(item.createdAt).toLocaleDateString("en-GB"),
        item.patientName,
        item.doctorName || "N/A",
        (item.netAmount || 0).toFixed(2),
        (item.dueAmount || 0).toFixed(2),
        `${item.status}${item.hasEditHistory ? ' (Modified)' : ''}`
    ].join("\t");
};

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Pathology"},
    { label: "Pathology Bills" },
  ];

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const skeletonColumns = [
    {
      width: 20,
      height: 16,
      isCircle: false,
      className: "px-3 py-5 print-hidden-in-print",
    }, // Checkbox
    { width: 80, height: 16, className: "px-3 py-5" },
    { width: 100, height: 16, className: "px-3 py-5" },
    { width: 150, height: 16, className: "px-3 py-5" }, 
    { width: 120, height: 16, className: "px-3 py-5" },
    { width: 100, height: 16, className: "px-3 py-5" },
    { width: 100, height: 16, className: "px-3 py-5" },
    { width: 100, height: 16, className: "px-3 py-5" },
    { width: 100, height: 16, className: "px-3 py-5" },
    { width: 80, height: 16, className: "px-3 py-5" },
    { width: 80, height: 16, className: "px-3 py-5" },
    { width: 80, height: 16, className: "px-3 py-5 print-hidden-in-print" },
  ];

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <div className="print-hidden">
        <PageHeading title="Pathology Bills" breadcrumbs={breadcrumbs} />
      </div>
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Bill List
            </h2>
            {!isReadOnly && selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <button
              onClick={() => {
                setSyncFilter((prev) => (prev === "pending" ? "all" : "pending"));
                setCurrentPage(1);
              }}
              className={`shrink-0 text-xs font-medium py-2 px-3 rounded-md border transition-all duration-200 ${
                syncFilter === "pending"
                  ? "bg-[#E7633D] text-white border-[#E7633D]"
                  : "bg-white text-gray-600 border-gray-200 hover:border-[#E7633D]"
              }`}
              title="Show only bills not yet synced"
            >
              {syncFilter === "pending" ? "Showing: Pending Sync" : "Pending Sync"}
            </button>
            <div className="w-32 shrink-0">
              <DynamicSelect
                options={[
                  { value: "all", label: "All Time" },
                  { value: "daily", label: "Daily" },
                  { value: "monthly", label: "Monthly" },
                  { value: "yearly", label: "Yearly" },
                ]}
                value={{ all: { value: "all", label: "All Time" }, daily: { value: "daily", label: "Daily" }, monthly: { value: "monthly", label: "Monthly" }, yearly: { value: "yearly", label: "Yearly" } }[dateFilterType]}
                onChange={(o) => { setDateFilterType(o.value); setCurrentPage(1); }}
                isSearchable={false}
                controlBackgroundColor="#ffffff"
                controlBorderColor="#e5e7eb"
              />
            </div>

            {dateFilterType === "daily" && (
              <div className="flex items-center gap-2 shrink-0">
                <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }} className="border rounded-md p-2 text-sm outline-none" />
                <span className="text-gray-400">to</span>
                <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }} className="border rounded-md p-2 text-sm outline-none" />
              </div>
            )}
            {dateFilterType === "monthly" && (
              <input type="month" value={selectedMonth} onChange={(e) => { setSelectedMonth(e.target.value); setCurrentPage(1); }} className="border rounded-md p-2 text-sm outline-none shrink-0" />
            )}
            {dateFilterType === "yearly" && (
              <input type="number" value={selectedYear} onChange={(e) => { setSelectedYear(e.target.value); setCurrentPage(1); }} className="border rounded-md p-2 text-sm outline-none w-24 shrink-0" min="2000" max="2100" />
            )}

            <div className="max-w-44 w-full shrink-0">
              <DynamicSelect
                options={filterOptions}
                value={filterOptions.find(opt => opt.value === statusFilter)}
                onChange={(selectedOption) => {
                  setStatusFilter(selectedOption.value);
                  setCurrentPage(1);
                }}
                isSearchable={false}
                placeholder="Filter Status"
                controlBackgroundColor="#ffffff"
                controlBorderColor="#e5e7eb"
              />
            </div>
            <input
              type="text"
              className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by Bill No, Name, Phone or ID..."
              value={searchQuery}
              onChange={handleSearchChange}
            />
            <div className="flex items-center justify-between w-full gap-3">
              {!isReadOnly && (
                <button
                  onClick={() => setGenerateBillModalOpen(true)}
                  className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
                >
                  Generate Bill
                </button>
              )}
              <AccountExportOptions
                data={allBillsForExport}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Pathology Bill Report"
                subTitle={getFilterSubtitle()}
                headers={headers}
                dataMapper={exportDataMapper}
                footer={billSummaryFooter}
                printedBy={user?.name || "Accounts"}
              />
            </div>
          </div>
        </div>
        <style>
          {`
            @media print {
              .print-hidden, .print-hidden-in-print {
                display: none !important;
              }
              .print-all-rows {
                display: table-row !important;
              }
              .print-table {
                width: 100%;
                border-collapse: collapse;
              }
              .print-table th, .print-table td {
                border: 1px solid #000;
                padding: 8px;
                text-align: left;
                font-size: 12px;
              }
              .print-table th {
                background-color: #f0f0f0;
                font-weight: bold;
              }
              .print-table tbody tr:nth-child(even) {
                background-color: #f9f9f9;
              }
            }
          `}
        </style>
        {!isReadOnly && (
            <AddBill
            isOpen={generateBillModalOpen || isEditModalOpen}
            onClose={() => {
                setGenerateBillModalOpen(false);
                setIsEditModalOpen(false);
                setBillToEdit(null);
            }}
            billDetails={billToEdit}
            isEditMode={isEditModalOpen}
            onSave={async (message, newBill) => {
                await handleRefreshBills();
                toast.success(message || "Bill added successfully!");
            }}
            />
        )}
        <div className="overflow-x-auto">
          <div className="print-wrapper">
            <table className="min-w-[1440px] w-full divide-y divide-gray-200 print-table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left print-hidden-in-print">
                    <div className="relative left-3 -top-[2px]">
                      {!isReadOnly && (
                          <>
                            <input
                                type="checkbox"
                                id="agreement"
                                className="hidden peer print-hidden"
                                onChange={handleSelectAll}
                                checked={
                                filteredBills.length > 0 &&
                                filteredBills.every((bill) =>
                                    selectedRows.includes(bill.id)
                                )
                                }
                            />
                            <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                                <FaCheck className="text-slate-100" />
                            </span>
                            <label
                                htmlFor="agreement"
                                className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                            ></label>
                          </>
                      )}
                    </div>
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    SL NO
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    BILL NO
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    DATE
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    PATIENT NAME
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    DOCTOR NAME
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    NET AMOUNT (৳)
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    DUE AMOUNT (৳)
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    STATUS
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    SUBMITTED BY
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    AUTHORIZED BY
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                    ACTIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <TableSkeleton
                    rowCount={itemsPerPage}
                    columns={skeletonColumns}
                    baseColor="#e5e7eb"
                    highlightColor="#f3f4f6"
                  />
                ) : filteredBills.length === 0 ? (
                  <tr className="print-hidden">
                    <td colSpan="11" className="text-center py-5 text-gray-500">
                      No bills found.
                    </td>
                  </tr>
                ) : (
                  <>
                    {filteredBills.map((bill, index) => {
                      // Log bills with undefined fields for debugging
                      if (
                        bill.netAmount === undefined ||
                        bill.paymentAmount === undefined ||
                        bill.dueAmount === undefined
                      ) {
                        console.warn("Bill with undefined fields:", {
                          id: bill.id,
                          billNumber: bill.billNumber,
                          netAmount: bill.netAmount,
                          paymentAmount: bill.paymentAmount,
                          dueAmount: bill.dueAmount,
                          status: bill.status,
                        });
                      }

                      return (
                        <tr
                          key={`normal-${bill.id}`}
                          className="border-b hover:bg-gray-50 print-hidden"
                        >
                          <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                            <div className="relative left-3 -top-[3px]">
                              {!isReadOnly && !bill.__offline && canDeleteBill(bill) && (
                                  <>
                                    <input
                                      type="checkbox"
                                      id={`bill-${index}`}
                                      className="hidden peer"
                                      checked={selectedRows.includes(bill.id)}
                                      onChange={() => handleRowSelect(bill.id)}
                                    />
                                    <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                                        <FaCheck className="text-slate-100" />
                                    </span>
                                    <label
                                        htmlFor={`bill-${index}`}
                                        className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                                    ></label>
                                  </>
                              )}
                            </div>
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                             {(currentPage - 1) * itemsPerPage + index + 1}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {bill.billNumber}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {new Date(bill.createdAt).toLocaleDateString("en-US")}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {bill.patientName}
                          </td>
                          
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {bill.doctorName || "N/A"}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {(bill.netAmount || 0).toFixed(2)}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            <div className="flex items-center gap-2">
                              <span>{(bill.dueAmount || 0).toFixed(2)}</span>
                              {bill.hasEditHistory && (
                                <div 
                                  className="size-4 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center cursor-help shrink-0"
                                  title="Payment history has corrections. View details to see notes."
                                >
                                  <span className="text-[10px] font-bold">!</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            <span
                                className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase border ${
                                  bill.status === "Complete"
                                    ? "bg-green-50 text-green-700 border-green-200"
                                    : bill.status === "Cancelled"
                                    ? "bg-red-50 text-red-700 border-red-200"
                                    : "bg-orange-50 text-orange-600 border-orange-200"
                                }`}
                              >
                                {bill.status}
                              </span>
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {(bill.userName || "Unknown").toLowerCase()}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {(bill.authorizedBy || "Unknown").toLowerCase()}
                          </td>
                          <td className="p-3 text-right print-hidden-in-print">
                              {bill.__offline ? (
                                <span
                                  className="px-2 py-1 rounded-full text-[10px] font-bold uppercase border bg-amber-50 text-amber-600 border-amber-200"
                                  title="This bill was created offline and will sync automatically when the server is reachable."
                                >
                                  Pending Sync
                                </span>
                              ) : (
                              <>
                              {/* Trigger Button */}
                              <button
                                ref={(el) => (dropdownTriggerRefs.current[bill.id] = el)}
                                onClick={() => setActiveDropdown(activeDropdown === bill.id ? null : bill.id)}
                                className="size-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors text-gray-500"
                              >
                                <BsThreeDotsVertical size={14} />
                              </button>

                              {/* Dropdown Menu */}
                              <SmartDropdown
                                isOpen={activeDropdown === bill.id}
                                triggerRef={{ current: dropdownTriggerRefs.current[bill.id] }}
                                onClose={() => setActiveDropdown(null)}
                                options={{ width: 160, align: "right" }}
                              >
                                <div className="py-1">
                                  {/* 1. Add Money Button */}
                                  <button
                                    onClick={() => {
                                      handleAddMoney(bill);
                                      setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors"
                                  >
                                    <FaMoneyBillWave className="text-green-600" size={14} />
                                    <span>Add Money</span>
                                  </button>

                                  {/* 2. View Details Button */}
                                  <button
                                    onClick={() => {
                                      handleView(bill);
                                      setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <FaEye className="text-blue-500" /> View Details
                                  </button>

                                  {/* Print Bill (A5 slip, same as Add Bill) */}
                                  <button
                                    onClick={() => {
                                      handlePrintBill(bill);
                                      setActiveDropdown(null);
                                    }}
                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <FaPrint className="text-indigo-500" /> Print Bill
                                  </button>

                                  {!isReadOnly && (
                                    <>
                                      {/* 3. Edit Bill Button */}
                                      <button
                                        onClick={() => {
                                          handleEdit(bill.id);
                                          setActiveDropdown(null);
                                        }}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                      >
                                        <FiEdit className="text-green-600" /> Edit Bill
                                      </button>
                                      <button
                                          onClick={() => bill.status !== "Cancelled" && (setBillToCancel(bill), setCancelModalOpen(true))}
                                          disabled={bill.status === "Cancelled"}
                                          className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${
                                            bill.status === "Cancelled" ? "opacity-40 cursor-not-allowed text-gray-400" : "text-orange-700 hover:bg-orange-50"
                                          }`}
                                        >
                                          <FaExchangeAlt className={bill.status === "Cancelled" ? "text-gray-400" : "text-orange-500"} size={14} /> 
                                          <span>{bill.status === "Cancelled" ? "Cancelled" : "Cancel Bill"}</span>
                                      </button>

                                        {/* 5. Delete Bill Button - Permanent removal */}
                                        {canDeleteBill(bill) && (
                                          <button
                                            onClick={() => {
                                              handleSingleDelete(bill.id);
                                              setActiveDropdown(null);
                                            }}
                                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600 flex items-center gap-2"
                                          >
                                            <RiDeleteBin6Line className="text-red-500" /> 
                                            <span>Delete Bill</span>
                                          </button>
                                        )}
                                    </>
                                  )}
                                </div>
                              </SmartDropdown>
                              </>
                              )}
                            </td>
                        </tr>
                      );
                    })}
                    {filteredBills.length > 0 ? (
                      filteredBills.map((bill, index) => (
                        <tr
                          key={`print-${bill.id}`}
                          className="border-b print-all-rows"
                          style={{ display: "none" }}
                        >
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3 print-hidden-in-print"></td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {bill.billNumber}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {new Date(bill.createdAt).toLocaleDateString("en-US")}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {bill.patientName}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {bill.doctorName || "N/A"}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {(bill.netAmount || 0).toFixed(2)}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {(bill.dueAmount || 0).toFixed(2)}
                          </td>
                          <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                            {bill.status || "Pending"}
                          </td>
                          <td className="p-3 text-left space-x-2 print-hidden-in-print"></td>
                        </tr>
                      ))
                    ) : (
                      <tr className="print-all-rows" style={{ display: "none" }}>
                        <td colSpan="10" className="text-center py-5 text-gray-500">
                          No bills found.
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <div className="print-hidden">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
      {/* Cancellation Reason Modal */}
      {cancelModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-2">Cancel Bill & Issue Refund</h3>
            <p className="text-sm text-gray-600 mb-4">
              You are about to cancel bill <strong>{billToCancel?.billNumber}</strong>. 
              This will mark the bill as "Cancelled" and log a refund in the history.
            </p>
            
            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">
              Reason for Cancellation
            </label>
            <textarea
              className="w-full p-3 border rounded-md outline-none focus:border-M-primary-color text-sm"
              placeholder="e.g., Patient decided not to do the test..."
              rows="3"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
            />
            
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setCancelModalOpen(false)}
                className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
              >
                Close
              </button>
              <button
                onClick={handleCancelBill}
                className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              >
                Confirm Cancellation
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="bill"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
        message={
          selectedRows.length === 1
            ? "Do you really want to delete this bill?"
            : `Do you really want to delete ${selectedRows.length} bills?`
        }
      />
      <BillDetailsModal
        isOpen={isBillDetailsModalOpen}
        onClose={() => setIsBillDetailsModalOpen(false)}
        billDetails={billDetails}
        onDelete={handleDeleteFromModal}
        onSave={async () => {
          await handleRefreshBills();
        }}
        isReadOnly={isReadOnly}
      />
      {printSlipData && (
        <PathologyPrintSlip
          className="pathology-list-print"
          title={printSlipData.title}
          billInfo={printSlipData.billInfo}
          table={printSlipData.table}
          summary={printSlipData.summary}
          footer={printSlipData.footer}
          isPaid={printSlipData.isPaid}
          isFree={printSlipData.isFree}
          note={printSlipData.note}
          amountInWords={printSlipData.amountInWords}
        />
      )}
      <PathologyAddPaymentModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          bill={paymentBillData}
          onPaymentSuccess={() => handleRefreshBills()}
        />
    </div>
  );
};

export default PathologyBill;